(function () {
  const sections = Array.from(document.querySelectorAll('[data-cursor-image-moment]'));
  if (!sections.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mobileQuery = window.matchMedia('(max-width: 990px)');

  const POSITION_LERP = 0.38;
  const ROTATION_LERP = 0.18;
  const ROTATION_DECAY = 0.88;
  const SWAP_COOLDOWN_MS = 450;
  const STACK_POSITIONS = ['front', 'back-right', 'back-left'];

  function parseImages(section) {
    try {
      const urls = JSON.parse(section.dataset.images || '[]');
      return Array.isArray(urls) ? urls.filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  function preload(urls) {
    return urls.map((url) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      return img;
    });
  }

  function distance(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function stackPositionOf(image) {
    if (image.classList.contains('cursor-image-moment-humm__stack-image--front')) return 'front';
    if (image.classList.contains('cursor-image-moment-humm__stack-image--back-right')) return 'back-right';
    return 'back-left';
  }

  function applyStackPosition(image, position) {
    image.classList.remove(
      'cursor-image-moment-humm__stack-image--front',
      'cursor-image-moment-humm__stack-image--back-right',
      'cursor-image-moment-humm__stack-image--back-left'
    );
    image.classList.add(`cursor-image-moment-humm__stack-image--${position}`);

    if (position === 'front') {
      image.setAttribute('aria-label', 'Front image');
      image.setAttribute('tabindex', '-1');
    } else {
      image.setAttribute('aria-label', 'Bring image to front');
      image.removeAttribute('tabindex');
    }
  }

  function endStackSwap(section, images, fallbackTimer) {
    if (fallbackTimer) {
      window.clearTimeout(fallbackTimer);
    }

    section.classList.remove('is-swapping');
    images.forEach((image) => {
      image.style.willChange = '';
    });
  }

  function bindMobileStack(section) {
    const stack = section.querySelector('[data-cim-mobile-stack]');
    const images = stack ? Array.from(stack.querySelectorAll('[data-cim-stack-image]')) : [];
    if (!stack || images.length < 2) return;

    images
      .sort((a, b) => STACK_POSITIONS.indexOf(stackPositionOf(a)) - STACK_POSITIONS.indexOf(stackPositionOf(b)))
      .forEach((image, index) => {
        applyStackPosition(image, STACK_POSITIONS[index] || 'back-left');
      });

    images.forEach((image) => {
      image.addEventListener('click', () => {
        if (image.classList.contains('cursor-image-moment-humm__stack-image--front')) return;
        if (section.classList.contains('is-swapping')) return;

        const currentOrder = Array.from(stack.querySelectorAll('[data-cim-stack-image]')).sort(
          (a, b) => STACK_POSITIONS.indexOf(stackPositionOf(a)) - STACK_POSITIONS.indexOf(stackPositionOf(b))
        );
        const nextOrder = [image].concat(currentOrder.filter((item) => item !== image));

        section.classList.add('is-swapping');
        images.forEach((item) => {
          item.style.willChange = 'transform';
        });

        nextOrder.forEach((item, index) => {
          applyStackPosition(item, STACK_POSITIONS[index] || 'back-left');
        });

        let pending = images.length;
        let fallbackTimer = window.setTimeout(() => {
          endStackSwap(section, images, fallbackTimer);
        }, 340);

        const onTransitionEnd = (event) => {
          if (event.propertyName !== 'transform') return;

          pending -= 1;
          if (pending <= 0) {
            images.forEach((item) => {
              item.removeEventListener('transitionend', onTransitionEnd);
            });
            endStackSwap(section, images, fallbackTimer);
          }
        };

        if (prefersReducedMotion.matches) {
          endStackSwap(section, images, fallbackTimer);
          return;
        }

        images.forEach((item) => {
          item.addEventListener('transitionend', onTransitionEnd);
        });
      });
    });
  }

  function setupDesktopCursor(section) {
    const stage = section.querySelector('.cursor-image-moment-humm__stage');
    const media = section.querySelector('.cursor-image-moment-humm__media');
    const imageA = section.querySelector('.cursor-image-moment-humm__image--a');
    const imageB = section.querySelector('.cursor-image-moment-humm__image--b');
    const urls = parseImages(section);
    if (!stage || !media || !imageA || !imageB || !urls.length) return;

    const preloaded = preload(urls);
    const layers = [imageA, imageB];
    let activeLayer = 0;

    const bounds = stage;
    const minTravel = parseInt(section.dataset.travelDistance, 10) || 120;
    const maxRotation = parseFloat(section.dataset.maxRotation) || 0;
    let currentIndex = 0;
    let loopId = 0;
    let isActive = false;
    let targetPointer = { x: 0, y: 0 };
    let displayPointer = { x: 0, y: 0 };
    let lastSwapPoint = null;
    let targetRotation = 0;
    let displayRotation = 0;
    let swapPending = false;
    let swappedThisFrame = false;
    let lastSwapAt = 0;
    let isSwapping = false;

    function activeImage() {
      return layers[activeLayer];
    }

    function inactiveImage() {
      return layers[1 - activeLayer];
    }

    function localPoint(event) {
      const rect = bounds.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    }

    function clampedPosition(point) {
      const rect = bounds.getBoundingClientRect();
      const mediaWidth = media.offsetWidth || imageA.width || 0;
      const mediaHeight = media.offsetHeight || imageA.height || 0;
      const x = clamp(point.x, 0, Math.max(0, rect.width - mediaWidth));
      const y = clamp(point.y, 0, Math.max(0, rect.height - mediaHeight));
      return { x, y };
    }

    function applyTransform(pos, rotation) {
      const x = Math.round(pos.x);
      const y = Math.round(pos.y);
      const rot = Math.round(rotation * 10) / 10;
      media.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${rot}deg)`;
    }

    function tick() {
      loopId = 0;
      if (!isActive) return;

      displayPointer.x += (targetPointer.x - displayPointer.x) * POSITION_LERP;
      displayPointer.y += (targetPointer.y - displayPointer.y) * POSITION_LERP;
      displayRotation += (targetRotation - displayRotation) * ROTATION_LERP;

      if (Math.abs(targetPointer.x - displayPointer.x) < 0.4) displayPointer.x = targetPointer.x;
      if (Math.abs(targetPointer.y - displayPointer.y) < 0.4) displayPointer.y = targetPointer.y;
      if (Math.abs(targetRotation - displayRotation) < 0.15) displayRotation = targetRotation;

      const pos = clampedPosition(displayPointer);
      applyTransform(pos, displayRotation);

      swappedThisFrame = false;
      if (swapPending && !isSwapping) {
        trySwap();
      }

      loopId = window.requestAnimationFrame(tick);
    }

    function startLoop() {
      if (loopId) return;
      loopId = window.requestAnimationFrame(tick);
    }

    function stopLoop() {
      if (!loopId) return;
      window.cancelAnimationFrame(loopId);
      loopId = 0;
    }

    function finishSwap(nextIndex) {
      const incoming = inactiveImage();
      const outgoing = activeImage();

      incoming.src = urls[nextIndex];
      incoming.classList.add('is-active');
      outgoing.classList.remove('is-active');

      activeLayer = 1 - activeLayer;
      currentIndex = nextIndex;
      isSwapping = false;
      swapPending = false;
      lastSwapPoint = { ...targetPointer };
      lastSwapAt = performance.now();
    }

    function trySwap() {
      if (urls.length < 2 || swappedThisFrame || isSwapping) return false;

      const now = performance.now();
      if (now - lastSwapAt < SWAP_COOLDOWN_MS) return false;

      const nextIndex = (currentIndex + 1) % urls.length;
      const candidate = preloaded[nextIndex];
      if (!candidate.complete) return false;

      isSwapping = true;
      swappedThisFrame = true;
      finishSwap(nextIndex);
      return true;
    }

    function updateTargetRotation(nextPoint, prevX) {
      const deltaX = nextPoint.x - prevX;
      if (Math.abs(deltaX) < 0.5) {
        targetRotation *= ROTATION_DECAY;
        return;
      }

      const nextTarget = clamp(deltaX * 0.14, -maxRotation, maxRotation);
      targetRotation += (nextTarget - targetRotation) * 0.35;
    }

    function onPointerMove(event) {
      if (!canHover.matches || prefersReducedMotion.matches || mobileQuery.matches) return;
      if (event.pointerType === 'mouse' && event.buttons !== 0) return;

      const nextPoint = localPoint(event);
      updateTargetRotation(nextPoint, targetPointer.x);
      targetPointer = nextPoint;

      if (!lastSwapPoint) {
        lastSwapPoint = nextPoint;
      } else if (!swapPending && !isSwapping && distance(nextPoint, lastSwapPoint) >= minTravel) {
        swapPending = true;
      }
    }

    function onPointerEnter(event) {
      if (!canHover.matches || prefersReducedMotion.matches || mobileQuery.matches) return;

      isActive = true;
      targetPointer = localPoint(event);
      displayPointer = { ...targetPointer };
      lastSwapPoint = { ...targetPointer };
      targetRotation = 0;
      displayRotation = 0;
      swapPending = false;
      isSwapping = false;
      lastSwapAt = 0;
      media.classList.add('is-visible');
      applyTransform(clampedPosition(displayPointer), 0);
      startLoop();
    }

    function onPointerLeave() {
      isActive = false;
      targetRotation = 0;
      displayRotation = 0;
      lastSwapPoint = null;
      swapPending = false;
      isSwapping = false;
      stopLoop();
      media.classList.remove('is-visible');
    }

    section.addEventListener('pointerenter', onPointerEnter);
    section.addEventListener('pointermove', onPointerMove);
    section.addEventListener('pointerleave', onPointerLeave);
    section.addEventListener('pointercancel', onPointerLeave);
  }

  function setupSection(section) {
    if (section.dataset.cimBound === '1') return;
    section.dataset.cimBound = '1';

    bindMobileStack(section);

    if (!mobileQuery.matches && canHover.matches && !prefersReducedMotion.matches) {
      setupDesktopCursor(section);
    }
  }

  sections.forEach(setupSection);

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target || !event.target.querySelectorAll) return;
    event.target.querySelectorAll('[data-cursor-image-moment]').forEach(setupSection);
  });
})();
